import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { GripVertical, Loader2, Lock, Milestone, Plus, Save, Trash2 } from 'lucide-react';
import useApi from '../api';
import AgentTabHeader from '../agents/tabs/AgentTabHeader';
import {
  LifecycleStageDef,
  STAGE_SWATCHES,
  StageBadge,
} from '../accounts/lifecycleStages';

/**
 * Org-level lifecycle stage editor (admin-gated Organization settings tab).
 *
 * Self-contained: fetches and saves via the dedicated
 * /accounts/lifecycle-stages endpoints, never through the cached UserContext
 * organization. System stages have fixed keys and a fixed relative order
 * (the server re-derives it on save regardless); custom stages interleave
 * anywhere between them.
 */

const slugifyKey = (label: string) =>
  label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);

/** Mirror of the server's normalization: system stages keep their canonical
 * relative order; custom stages keep the slot they were dropped into. */
const enforceSystemOrder = (
  rows: LifecycleStageDef[],
  canonicalSystemKeys: string[]
): LifecycleStageDef[] => {
  const systemByKey = new Map(rows.filter((r) => r.system).map((r) => [r.key, r]));
  const systemQueue = canonicalSystemKeys.filter((key) => systemByKey.has(key));
  let index = 0;
  return rows.map((row) => (row.system ? systemByKey.get(systemQueue[index++])! : row));
};

const ColorSwatchPicker = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (color: string) => void;
}) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button
        type="button"
        className={`h-5 w-5 rounded-full ${STAGE_SWATCHES[value] || STAGE_SWATCHES.gray} ring-offset-background transition-shadow hover:ring-2 hover:ring-ring hover:ring-offset-2`}
        title="Stage color"
      />
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start" className="min-w-0">
      <div className="grid grid-cols-3 gap-1.5 p-1">
        {Object.keys(STAGE_SWATCHES).map((token) => (
          <DropdownMenuItem key={token} className="p-1 focus:bg-transparent" onClick={() => onChange(token)}>
            <span
              className={`h-5 w-5 rounded-full ${STAGE_SWATCHES[token]} ${token === value ? 'ring-2 ring-ring ring-offset-1' : ''}`}
              title={token}
            />
          </DropdownMenuItem>
        ))}
      </div>
    </DropdownMenuContent>
  </DropdownMenu>
);

const SortableStageRow = ({
  stage,
  allStages,
  onLabelChange,
  onColorChange,
  onDelete,
}: {
  stage: LifecycleStageDef;
  allStages: LifecycleStageDef[];
  onLabelChange: (key: string, label: string) => void;
  onColorChange: (key: string, color: string) => void;
  onDelete: (key: string) => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: stage.key,
    disabled: stage.system,
  });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-center gap-3 rounded-lg border bg-card px-3 py-2 ${isDragging ? 'opacity-60 shadow-lg' : ''}`}
    >
      {stage.system ? (
        <span title="System stage — fixed position, cannot be deleted">
          <Lock className="h-4 w-4 shrink-0 text-muted-foreground/60" />
        </span>
      ) : (
        <button
          type="button"
          className="cursor-grab touch-none text-muted-foreground/60 hover:text-muted-foreground"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4 shrink-0" />
        </button>
      )}
      <ColorSwatchPicker value={stage.color} onChange={(color) => onColorChange(stage.key, color)} />
      <Input
        value={stage.label}
        onChange={(e) => onLabelChange(stage.key, e.target.value)}
        maxLength={60}
        className="h-8 max-w-[220px]"
      />
      <StageBadge stageKey={stage.key} stages={allStages} />
      <code className="text-[11px] text-muted-foreground">{stage.key}</code>
      <div className="ml-auto flex items-center gap-2">
        {!stage.system && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-destructive"
            onClick={() => onDelete(stage.key)}
            title="Delete custom stage"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default function LifecycleStages() {
  const { get, put } = useApi();
  const [rows, setRows] = useState<LifecycleStageDef[]>([]);
  const [canonicalSystemKeys, setCanonicalSystemKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newLabel, setNewLabel] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    get('/accounts/lifecycle-stages')
      .then((response) => {
        const stages: LifecycleStageDef[] = response.data?.stages || [];
        setRows(stages);
        setCanonicalSystemKeys(stages.filter((s) => s.system).map((s) => s.key));
      })
      .catch((err) => {
        console.error('Error fetching lifecycle stages:', err);
        setError('Failed to load lifecycle stages');
      })
      .finally(() => setLoading(false));
  }, []);

  const newKey = useMemo(() => slugifyKey(newLabel), [newLabel]);
  const newKeyTaken = useMemo(() => rows.some((r) => r.key === newKey), [rows, newKey]);

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = rows.findIndex((r) => r.key === active.id);
    const newIndex = rows.findIndex((r) => r.key === over.id);
    setRows(enforceSystemOrder(arrayMove(rows, oldIndex, newIndex), canonicalSystemKeys));
  };

  const addStage = () => {
    if (!newKey || newKeyTaken) return;
    setRows([
      ...rows,
      { key: newKey, label: newLabel.trim(), color: 'gray', order: (rows.length + 1) * 10, system: false },
    ]);
    setNewLabel('');
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const response = await put('/accounts/lifecycle-stages', {
        stages: rows.map((row, index) => ({ ...row, order: (index + 1) * 10 })),
      });
      const stages: LifecycleStageDef[] = response.data?.stages || [];
      setRows(stages);
      toast.success('Lifecycle stages saved');
    } catch (err: any) {
      const detail = err?.data?.detail || 'Failed to save lifecycle stages';
      setError(detail);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[350px] items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
      <AgentTabHeader
        icon={Milestone}
        title="Lifecycle Stages"
        description="Define the lifecycle stages accounts move through."
        actions={
          <Button type="button" size="sm" className="gap-2" onClick={save} disabled={saving}>
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            <span className="hidden sm:inline">Save changes</span>
            <span className="sm:hidden">Save</span>
          </Button>
        }
      />
      <div className="mx-auto w-full max-w-4xl space-y-6 py-6">
        <section className="space-y-3">
          <div>
            <h3 className="text-sm font-semibold">Stages</h3>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              Automatic signals only move accounts forward through system stages — a customer is
              never demoted because a teammate books a demo. Manual edits, your product's SDK, and
              (in the future) CRM sync can move accounts anywhere. System stages are locked in
              place; drag custom stages between them.
            </p>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={rows.map((r) => r.key)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {rows.map((stage) => (
                  <SortableStageRow
                    key={stage.key}
                    stage={stage}
                    allStages={rows}
                    onLabelChange={(key, label) =>
                      setRows(rows.map((r) => (r.key === key ? { ...r, label } : r)))
                    }
                    onColorChange={(key, color) =>
                      setRows(rows.map((r) => (r.key === key ? { ...r, color } : r)))
                    }
                    onDelete={(key) => setRows(rows.filter((r) => r.key !== key))}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </section>

        <section className="space-y-3">
          <div>
            <h3 className="text-sm font-semibold">Add a custom stage</h3>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              Custom stages are set manually or via your SDK — automatic signals never move
              accounts into or out of them. The key is derived from the label once and cannot be
              changed after saving.
            </p>
          </div>
          <div className="flex items-center gap-3 rounded-lg border bg-card p-3">
            <Input
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="e.g. Evaluating"
              maxLength={60}
              className="h-8 max-w-[220px]"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addStage();
                }
              }}
            />
            {newKey && (
              <code className="text-[11px] text-muted-foreground">{newKey}</code>
            )}
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="ml-auto gap-1.5"
              onClick={addStage}
              disabled={!newKey || newKeyTaken}
            >
              <Plus className="h-3.5 w-3.5" /> Add stage
            </Button>
          </div>
          {newKeyTaken && (
            <p className="text-xs text-destructive">A stage with the key “{newKey}” already exists.</p>
          )}
        </section>
      </div>
    </>
  );
}
